import { observable, computed, makeObservable } from 'mobx'
import flatten from 'lodash/flatten'
import { Permission } from '../dtos/permissions'
import { TeamMemberDTO } from '../dtos/team-member'
import { Model } from 'infra/model'
import { DateZero, maybeParseDate } from 'utils/date-util'
import Member from './Member'
import Team from './Team'
import TeamRole from './TeamRole'
import { PrimeMember } from 'iam/MemberStore'
import { PrimeTeam } from 'iam/TeamStore'
import { PrimeTeamRole } from 'iam/TeamRoleStore'
import { Id } from 'types/definitions'
import once from 'lodash/once'

/**
 * A member of a team.
 */
export default class TeamMember extends Model<TeamMemberDTO, TeamMember> {
  @observable workspaceId = Id.none
  @observable inactive = false
  @observable dateCreated: Date = DateZero()
  @observable dateModified: Date = DateZero()
  @observable member = Member.none
  @observable team = Team.none
  @observable teamRole = TeamRole.none

  static get none(): TeamMember {
    return lazyNone()
  }

  constructor(
    private primeMember: PrimeMember,
    private primeTeam: PrimeTeam,
    private primeTeamRole: PrimeTeamRole
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get computedPermissions() {
    return this.teamRole.permissions
  }

  @computed
  get isTeamOwner() {
    return this.team.owner.member === this.member
  }

  hasAllPermissions(...ids: Array<Permission>) {
    ids = flatten(ids)
    if (ids.length === 0) return true
    return ids.every((id) => this.hasPermission(id))
  }

  hasSomePermissions(...ids: Array<Permission>) {
    ids = flatten(ids)
    if (ids.length === 0) return true
    return ids.some((id) => this.hasPermission(id))
  }

  hasPermission(id: Permission) {
    const computedPerms = this.computedPermissions || {}
    return computedPerms[id] === 'allow'
  }

  /**
   * Parses the props.
   *
   * @param {*} json
   */
  parse(json: Partial<TeamMemberDTO>): Partial<TeamMember> {
    return {
      inactive: json.inactive,
      workspaceId: json.workspace_id,
      dateCreated: maybeParseDate(json.date_created),
      dateModified: maybeParseDate(json.date_modified),
      teamRole: this.primeTeamRole(json.team_role),
      team: this.primeTeam({ id: json.team_id, ...json.team }),
      member: this.primeMember({
        workspace_id: json.workspace_id || this.workspaceId,
        user_id: json.user_id,
        user_public_id: json.user_public_id,
      }),
    }
  }
}

const lazyNone = once(
  () =>
    new TeamMember(
      () => Member.none,
      () => Team.none,
      () => TeamRole.none
    )
)
